import React, { useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import ReactMarkdown from 'react-markdown';

// Mapeo de elementos Markdown -> componentes con estilos compactos para la burbuja.
// Se descarta `node` (prop interna de react-markdown) para no propagarla al DOM.
const mdComponents = {
  p: ({ node, ...props }) => (
    <Typography variant="body2" sx={{ lineHeight: 1.5, my: 0.5 }} {...props} />
  ),
  ul: ({ node, ...props }) => (
    <Box component="ul" sx={{ pl: 2.5, my: 0.5, fontSize: '0.875rem', lineHeight: 1.5 }} {...props} />
  ),
  ol: ({ node, ...props }) => (
    <Box component="ol" sx={{ pl: 2.5, my: 0.5, fontSize: '0.875rem', lineHeight: 1.5 }} {...props} />
  ),
  li: ({ node, ...props }) => <Box component="li" sx={{ mb: 0.25 }} {...props} />,
  strong: ({ node, ...props }) => <strong style={{ fontWeight: 700 }} {...props} />,
  em: ({ node, ...props }) => <em {...props} />,
  a: ({ node, ...props }) => (
    <a style={{ color: '#4f46e5' }} target="_blank" rel="noreferrer" {...props} />
  ),
  h1: ({ node, ...props }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }} {...props} />
  ),
  h2: ({ node, ...props }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }} {...props} />
  ),
  h3: ({ node, ...props }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }} {...props} />
  ),
  code: ({ node, ...props }) => (
    <Box
      component="code"
      sx={{ bgcolor: '#e2e8f0', px: 0.5, borderRadius: 0.5, fontSize: '0.8125rem' }}
      {...props}
    />
  ),
};

/**
 * Lista de mensajes del chat con el asistente.
 *
 * Cada mensaje tiene la forma { id, role: 'user' | 'assistant', content, isError }.
 * El mensaje del asistente en curso se va rellenando token a token desde el
 * componente padre (que muta su `content`).
 *
 * Mientras el agente usa una tool se muestra un indicador sutil de actividad
 * (`activeTool`), p.ej. "Consultando tu menú…".
 *
 * Props:
 *  - messages: array de mensajes
 *  - loading: boolean (respuesta en streaming en curso)
 *  - activeTool: string|null (etiqueta legible de la tool activa, o null)
 */

// Burbuja individual de mensaje.
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  // El texto del asistente (no error) se renderiza como Markdown; el del usuario
  // y los errores, como texto plano.
  const renderMarkdown = !isUser && !message.isError && Boolean(message.content);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          px: 1.75,
          py: 1.25,
          borderRadius: 2.5,
          // Esquina "pinchada" hacia su lado, como en chats habituales.
          borderBottomRightRadius: isUser ? 6 : 20,
          borderBottomLeftRadius: isUser ? 20 : 6,
          bgcolor: isUser
            ? '#4f46e5'
            : message.isError
            ? '#fef2f2'
            : '#f1f5f9',
          color: isUser ? '#fff' : message.isError ? '#b91c1c' : '#1e293b',
          border: message.isError ? '1px solid #fecaca' : 'none',
          boxShadow: isUser ? '0 2px 6px rgba(79,70,229,0.25)' : 'none',
          // En texto plano respetamos saltos de línea; en Markdown lo gestiona el render.
          whiteSpace: renderMarkdown ? 'normal' : 'pre-wrap',
          wordBreak: 'break-word',
          // Quita el margen sobrante del primer/último bloque Markdown.
          '& > *:first-of-type': { mt: 0 },
          '& > *:last-child': { mb: 0 },
        }}
      >
        {renderMarkdown ? (
          <ReactMarkdown components={mdComponents}>{message.content}</ReactMarkdown>
        ) : (
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            {message.content || (
              // Mensaje del asistente aún sin texto: micro-cursor de espera.
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#94a3b8',
                  animation: 'nutriPulse 1s ease-in-out infinite',
                  '@keyframes nutriPulse': {
                    '0%, 100%': { opacity: 0.3 },
                    '50%': { opacity: 1 },
                  },
                }}
              />
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const MessageList = ({ messages, loading, activeTool }) => {
  const bottomRef = useRef(null);

  // Auto-scroll al final cada vez que cambian los mensajes, el estado de carga
  // o la tool activa (para mantener visible la respuesta en streaming).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading, activeTool]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        px: 2,
        py: 2,
        bgcolor: '#fff',
      }}
    >
      {messages.length === 0 && (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#94a3b8',
            px: 3,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5 }}>
            ¿En qué puedo ayudarte?
          </Typography>
          <Typography variant="caption">
            Pregúntame por tu menú semanal, recetas, ingredientes o tu seguimiento.
          </Typography>
        </Box>
      )}

      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} />
      ))}

      {/* Indicador de actividad de tool entre tool_start y tool_end. */}
      {activeTool && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#64748b',
            mb: 1.5,
            ml: 0.5,
          }}
        >
          <CircularProgress size={14} thickness={5} sx={{ color: '#4f46e5' }} />
          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
            {activeTool}
          </Typography>
        </Box>
      )}

      <div ref={bottomRef} />
    </Box>
  );
};

export default MessageList;
